package com.sddamico.swigmore.util

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.sddamico.swigmore.api.PollApi
import com.sddamico.swigmore.models.MeResponse
import kotlinx.coroutines.CancellationException

/**
 * The current session, as the server reports it. A [LoadState.Ready] with a `null` value
 * means signed out. There is one for the whole app, so a page reached through the nav starts from
 * the session the last page knew instead of from [LoadState.Loading].
 */
class AuthState internal constructor() {
  var value: LoadState<MeResponse?> by mutableStateOf(LoadState.Loading)
    private set

  /** Re-reads the session. A failure leaves a signed-in session in place rather than blanking the page. */
  suspend fun refresh() {
    val fetched = try {
      LoadState.Ready(PollApi.me())
    } catch (e: CancellationException) {
      throw e
    } catch (e: Throwable) {
      LoadState.Failed(e.errorText())
    }
    if (fetched is LoadState.Ready || (value as? LoadState.Ready)?.value == null) value = fetched
    if (fetched is LoadState.Ready && fetched.value == null) signOutHooks.forEach { it() }
  }

  /**
   * Signs out on the server (ignoring any error: the session is cleared client-side either way)
   * and un-enrolls this browser from push, so poll alerts for the account stop arriving here.
   */
  suspend fun logout() {
    val pushEndpoint = currentPushEndpoint()
    try {
      PollApi.logout(pushEndpoint)
      // Only once the server has let go of it: if the sign-out did not land, the session is still
      // live and should keep its alerts.
      releasePushSubscription()
    } catch (_: Throwable) {
    }
    signOutHooks.forEach { it() }
    value = LoadState.Ready(null)
  }
}

private val signOutHooks = mutableListOf<() -> Unit>()

/** Runs [forget] whenever the session ends: for state kept across pages that belongs to one account. */
fun forgetOnSignOut(forget: () -> Unit) {
  signOutHooks += forget
}

private val appAuth = AuthState()

/** The app's [AuthState], re-checked against the server each time a page mounts. */
@Composable
fun rememberAuthState(): AuthState {
  LaunchedEffect(Unit) { appAuth.refresh() }
  return appAuth
}
